import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Loader2, Flame, Crown, Clock, ChevronLeft } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { NFTCard } from "@/components/marketplace/NFTCard";
import { useMarketplaceNFTs, MarketplaceNFT } from "@/hooks/useMarketplaceNFTs";
import { supabase } from "@/integrations/supabase/client";
import { PAGE_BG_STYLE } from "@/lib/pageBg";

const CATEGORIES = {
  "/marketplace/recent": { title: "Recent", icon: Clock, color: "text-primary", mode: "recent" as const },
  "/marketplace/trending": { title: "Trending (24h)", icon: Flame, color: "text-orange-500", mode: "trending" as const },
  "/marketplace/most-expensive": { title: "Most Expensive", icon: Crown, color: "text-yellow-600", mode: "expensive" as const },
};

export default function MarketplaceCategory() {
  const { nfts, loading, refresh } = useMarketplaceNFTs();
  const location = useLocation();
  const cat = CATEGORIES[location.pathname as keyof typeof CATEGORIES] ?? CATEGORIES["/marketplace/recent"];
  const [trending24h, setTrending24h] = useState<Map<string, number>>(new Map());

  useEffect(() => {
    if (cat.mode !== "trending") return;
    (async () => {
      const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const { data } = await supabase.from("nft_ownership_history").select("nft_id").gte("created_at", since);
      const counts = new Map<string, number>();
      (data || []).forEach((t: any) => { if (t.nft_id) counts.set(t.nft_id, (counts.get(t.nft_id) || 0) + 1); });
      setTrending24h(counts);
    })();
  }, [cat.mode]);

  const sorted: MarketplaceNFT[] = useMemo(() => {
    const arr = [...nfts];
    if (cat.mode === "recent") return arr.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    if (cat.mode === "expensive") return arr.sort((a, b) => (b.current_price || b.price) - (a.current_price || a.price));
    return arr
      .map((n) => ({ n, c: trending24h.get(n.id) || 0 }))
      .filter((x) => x.c > 0)
      .sort((a, b) => b.c - a.c)
      .map((x) => x.n);
  }, [nfts, cat.mode, trending24h]);

  const Icon = cat.icon;

  return (
    <div className="min-h-screen flex flex-col overflow-x-hidden relative" style={PAGE_BG_STYLE}>
      <AppHeader currentPage="earn" />
      <main className="flex-1 pt-28 pb-24 px-3 relative z-10">
        <div className="max-w-6xl mx-auto space-y-4">
          <div className="flex items-center justify-between">
            <Link to="/marketplace" className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
              <ChevronLeft className="w-4 h-4" /> Back
            </Link>
            <h1 className={`text-base font-extrabold flex items-center gap-2 ${cat.color}`}>
              <Icon className="w-4 h-4" /> {cat.title}
            </h1>
            <span className="text-xs text-muted-foreground">{sorted.length} NFTs</span>
          </div>

          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="w-10 h-10 animate-spin text-primary" /></div>
          ) : sorted.length === 0 ? (
            <p className="text-center py-16 text-sm text-muted-foreground">No NFTs in this category</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {sorted.map((nft) => (
                <NFTCard key={nft.id} nft={nft} showTrending={cat.mode === "trending"} onBuySuccess={refresh} />
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
